import { storableError, isTooManyRequestsError } from '../util/errors';
import { denormalisedResponseEntities } from '../util/data';
import { addMarketplaceEntities } from './marketplaceData.duck';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER } from '../util/types';
import {
  isSdk429Error,
  isSdkFetchCached,
  isSdkRateLimited,
  sdkRateLimitUntil,
} from '../util/sdkRateLimit';
import { isGlobalSdkRateLimited, scheduleSdkRequest } from '../util/sdkRequestQueue';

// ================ Action types ================ //

export const FETCH_USER_REVIEWS_REQUEST = 'app/userReviews/FETCH_USER_REVIEWS_REQUEST';
export const FETCH_USER_REVIEWS_SUCCESS = 'app/userReviews/FETCH_USER_REVIEWS_SUCCESS';
export const FETCH_USER_REVIEWS_ERROR = 'app/userReviews/FETCH_USER_REVIEWS_ERROR';

// ================ Reducer ================ //

const initialState = {
  userReviews: {}, // userId -> { reviews, loading, error, fetchedAt, rateLimitedUntil }
};

export default function userReviewsReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_USER_REVIEWS_REQUEST:
      return {
        ...state,
        userReviews: {
          ...state.userReviews,
          [payload.userId]: {
            ...state.userReviews[payload.userId],
            loading: true,
            error: null,
          },
        },
      };

    case FETCH_USER_REVIEWS_SUCCESS:
      return {
        ...state,
        userReviews: {
          ...state.userReviews,
          [payload.userId]: {
            reviews: payload.reviews,
            loading: false,
            error: null,
            fetchedAt: payload.fetchedAt,
            rateLimitedUntil: null,
          },
        },
      };

    case FETCH_USER_REVIEWS_ERROR:
      return {
        ...state,
        userReviews: {
          ...state.userReviews,
          [payload.userId]: {
            ...state.userReviews[payload.userId],
            reviews: state.userReviews[payload.userId]?.reviews || [],
            loading: false,
            error: payload.error,
            fetchedAt: payload.fetchedAt ?? state.userReviews[payload.userId]?.fetchedAt,
            rateLimitedUntil: payload.rateLimitedUntil ?? null,
          },
        },
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchUserReviewsRequest = userId => ({
  type: FETCH_USER_REVIEWS_REQUEST,
  payload: { userId },
});

export const fetchUserReviewsSuccess = (userId, reviews, fetchedAt = Date.now()) => ({
  type: FETCH_USER_REVIEWS_SUCCESS,
  payload: { userId, reviews, fetchedAt },
});

export const fetchUserReviewsError = (userId, error, extra = {}) => ({
  type: FETCH_USER_REVIEWS_ERROR,
  payload: { userId, error, ...extra },
});

// ================ Thunks ================ //

const inFlightByUserId = new Map();

const filterReviewsByType = (reviews, reviewType) =>
  reviewType ? reviews.filter(review => review.attributes.type === reviewType) : reviews;

export const fetchUserReviews = (userId, reviewType = REVIEW_TYPE_OF_PROVIDER) => (
  dispatch,
  getState,
  sdk
) => {
  const userIdKey = userId?.uuid || userId;
  if (!userIdKey) {
    return Promise.resolve([]);
  }

  const state = getState();
  const cached = state.userReviews?.userReviews?.[userIdKey];

  if (cached?.loading && inFlightByUserId.has(userIdKey)) {
    return inFlightByUserId.get(userIdKey);
  }

  if (isSdkRateLimited(cached?.rateLimitedUntil) || isGlobalSdkRateLimited()) {
    return Promise.resolve(cached?.reviews || []);
  }

  if (isSdkFetchCached(cached?.fetchedAt)) {
    return Promise.resolve(filterReviewsByType(cached?.reviews || [], reviewType));
  }

  if (inFlightByUserId.has(userIdKey)) {
    return inFlightByUserId.get(userIdKey);
  }

  dispatch(fetchUserReviewsRequest(userIdKey));

  const request = scheduleSdkRequest(() =>
    sdk.reviews.query({
      subject_id: userIdKey,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
  )
    .then(response => {
      dispatch(addMarketplaceEntities(response));

      const reviews = denormalisedResponseEntities(response);
      const filteredReviews = filterReviewsByType(reviews, reviewType);

      dispatch(fetchUserReviewsSuccess(userIdKey, filteredReviews));
      return filteredReviews;
    })
    .catch(e => {
      const error = storableError(e);
      const now = Date.now();
      const rateLimited = isSdk429Error(e) || isTooManyRequestsError(error);

      dispatch(
        fetchUserReviewsError(userIdKey, error, {
          fetchedAt: now,
          rateLimitedUntil: rateLimited ? sdkRateLimitUntil(now) : null,
        })
      );

      return [];
    })
    .finally(() => {
      inFlightByUserId.delete(userIdKey);
    });

  inFlightByUserId.set(userIdKey, request);
  return request;
};

// ================ Selectors ================ //

const selectorCache = new Map();

const createMemoizedSelector = (selectorFn, key) => {
  if (!selectorCache.has(key)) {
    selectorCache.set(key, { lastState: null, lastResult: null });
  }

  return (state, ...args) => {
    const cache = selectorCache.get(key);
    const stateKey = JSON.stringify({ state: state.userReviews, args });

    if (cache.lastState === stateKey) {
      return cache.lastResult;
    }

    cache.lastState = stateKey;
    cache.lastResult = selectorFn(state, ...args);
    return cache.lastResult;
  };
};

export const getUserReviews = createMemoizedSelector((state, userId) => {
  return state.userReviews?.userReviews?.[userId] || { reviews: [], loading: false, error: null };
}, 'getUserReviews');

export const getUserReviewsData = createMemoizedSelector((state, userId) => {
  return getUserReviews(state, userId).reviews || [];
}, 'getUserReviewsData');

export const getUserReviewsLoading = createMemoizedSelector((state, userId) => {
  return getUserReviews(state, userId).loading || false;
}, 'getUserReviewsLoading');

export const getUserReviewsError = createMemoizedSelector((state, userId) => {
  return getUserReviews(state, userId).error || null;
}, 'getUserReviewsError');
